/*!
  Pioneer WFRIA CountdownTimer v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["CountdownTimer"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("CountdownTimer") < 0) {
    window.__PIONEER_GLOBAL__.components.push("CountdownTimer");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import PropTypes from "prop-types";
import React from "react";
import LiveRegion from "../private/LiveRegion/LiveRegion";
import Timer from "../Timer/Timer";
import { ANNOUNCEMENT_DELAY, ONE_SECOND, THIRTY_SECONDS } from "./constants";
import STRINGS from "./strings";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const TIMER_ANNOUNCEMENTS = [THIRTY_SECONDS, 20 * ONE_SECOND, 10 * ONE_SECOND, 7 * ONE_SECOND, 4 * ONE_SECOND, ONE_SECOND, 0];
const TIMER_ANNOUNCEMENTS_MAP = TIMER_ANNOUNCEMENTS.reduce((map, step) => {
  map[step] = true;
  return map;
}, {});
const ONLY_ANNOUNCE_SECONDS_UPPER_BOUND = 9;
/**
 * The `CountdownTimer` component provides a simple, overridable UI for rendering an accessible Timer.
 * It is responsible for rendering the visual UI, as well as hidden content into an ARIA Live Region.
 */

function CountdownTimer(_ref) {
  let {
    alwaysRenderCustomLiveRegion,
    format,
    onComplete,
    politeness,
    render,
    renderLiveRegion,
    role,
    timeLimit,
    timerState,
    announcementDelay,
    renderFocusableContent
  } = _ref;
  return _jsx(Timer, {
    format: format,
    interval: 1,
    onComplete: onComplete,
    render: timer => {
      const {
        time: {
          minutes,
          seconds
        },
        timeInMilliseconds
      } = timer;
      const isLessThanOneMinute = typeof minutes === "undefined" || minutes <= 0; // it's possible to hit negative time when we start with `timeLimit={0}`

      const isComplete = isLessThanOneMinute && seconds <= 0;
      const content = render && render(timer, isComplete);
      /**
       * Per our a11y specifications:
       *  > With 10 seconds remaining, announce once 3 seconds until the timer expires.
       *  > Only the seconds remaining should be announced, e.g. "7,4,1".
       */

      const shouldOnlyAnnounceSeconds = !isComplete && isLessThanOneMinute && seconds <= ONLY_ANNOUNCE_SECONDS_UPPER_BOUND;
      let assistiveContent;

      if (renderLiveRegion !== null && (alwaysRenderCustomLiveRegion || !shouldOnlyAnnounceSeconds)) {
        assistiveContent = renderLiveRegion(timer, isComplete);
      } else if (shouldOnlyAnnounceSeconds) {
        assistiveContent = seconds;
      } else {
        assistiveContent = content;
      }

      const shouldAnnounce = timeInMilliseconds < THIRTY_SECONDS ? TIMER_ANNOUNCEMENTS_MAP[timeInMilliseconds] : timeInMilliseconds % THIRTY_SECONDS === 0;
      return _jsxs(React.Fragment, {
        children: [_jsx(LiveRegion, {
          politeness: politeness,
          role: role,
          announcementDelay: announcementDelay,
          children: shouldAnnounce && assistiveContent
        }), !shouldAnnounce && _jsx("div", {
          className: "wf-u-visually-hidden",
          children: assistiveContent
        }), _jsx("div", {
          "aria-hidden": "true",
          children: content
        }), renderFocusableContent && renderFocusableContent()]
      });
    },
    timeLimit: timeLimit,
    timerState: timerState
  });
}

CountdownTimer.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Our a11y specifications prescribe that between 9 seconds and 1 second, we only announce the number
   * once every 3 seconds i.e. "7,4,1".
   *
   * By default, even if you supply a `renderLiveRegion` function, we ignore it for those final seconds.
   * Set `alwaysRenderCustomLiveRegion` to `true` in order to override this feature, and always render
   * the result of `renderLiveRegion`.
   */
  alwaysRenderCustomLiveRegion: PropTypes.bool,

  /**
   * @ignore
   * The time after which the live region content should be announced.
   */
  announcementDelay: PropTypes.number,

  /**
   * See Timer's Documentation.
   */
  format: Timer.propTypes.format,

  /**
   * See Timer's Documentation.
   */
  onComplete: Timer.propTypes.onComplete,

  /**
   * The politeness setting to use for the live region's announcements.
   */
  politeness: PropTypes.oneOf(["assertive", "polite"]),

  /**
   * A Function that follows the render props pattern and receives 2 arguments:
   *
   * @param {Object} timer
   *   @param {String} timer.formattedTime
   *   @param {Object} timer.time
   *     @param {Number} timer.time.minutes
   *     @param {Number} timer.time.seconds
   * @param {Boolean} isComplete
   */
  render: PropTypes.func,

  /**
   * @ignore
   *
   * This prop is to render any focusable elements such as buttons in the WaitMessage container.
   * So that, screen readers will recognize and announce it appropriately.
   */
  renderFocusableContent: PropTypes.func,

  /**
   * A Function that follows the render props pattern.
   *
   * > This may not behave as you expect when the remaining time is between 9 seconds and 1 second. See the
   * > `alwaysRenderCustomLiveRegion` documentation for details.
   *
   * By default we use the same value returned by `props.render`, except when the remaining time is between
   * 9 seconds and 1 second. When the remaining time is within that interval, we only render the number.
   *
   * @param {Object} timer
   *   @param {String} timer.formattedTime
   *   @param {Object} timer.time
   *     @param {Number} timer.time.minutes
   *     @param {Number} timer.time.seconds
   * @param {Boolean} isComplete
   */
  renderLiveRegion: PropTypes.func,

  /**
   * The value to assign to the "role" attribute.
   */
  role: PropTypes.string,

  /**
   * See Timer's Documentation.
   */
  timeLimit: Timer.propTypes.timeLimit,

  /**
   * See Timer's Documentation.
   */
  timerState: Timer.propTypes.timerState
} : {};
CountdownTimer.defaultProps = {
  alwaysRenderCustomLiveRegion: false,
  format: Timer.defaultProps.format,
  onComplete: Timer.defaultProps.onComplete,
  politeness: "assertive",
  render: (_ref2, isComplete) => {
    let {
      formattedTime
    } = _ref2;
    return _jsx("span", {
      className: "wf-countdown-timer--content",
      children: isComplete ? STRINGS.completionMessage : formattedTime
    });
  },
  renderLiveRegion: null,
  role: "timer",
  timeLimit: Timer.defaultProps.timeLimit,
  timerState: Timer.defaultProps.timerState,

  /**
   * When we start the timer, live region element and text is rendered to DOM at once
   * so in order to make the screenreaders announce the start time this slight delay
   * is required.
   */
  announcementDelay: ANNOUNCEMENT_DELAY,
  renderFocusableContent: null
};
CountdownTimer.displayName = "Timer";
export default /*#__PURE__*/React.memo(CountdownTimer);